import { doc, setDoc, Timestamp } from 'firebase/firestore';
import { createUserWithEmailAndPassword, updateCurrentUser } from 'firebase/auth';
import { db, auth } from '../../../../../firebase/config';

export type PSOFormData = {
    name: string;
    contact: string;
    email: string;
    designation: string;
    password?: string;
    confirmPassword?: string;
};

/** Same list as Add Staff — used for edit designation in PSODetailsModal. */
export const PSO_DESIGNATION_OPTIONS: { value: string; label: string }[] = [
    { value: 'Head', label: 'Head' },
    { value: 'Regional Head', label: 'Regional Head' },
    { value: 'PSO', label: 'PSO' },
    { value: 'Protocol Officer', label: 'Protocol Officer' },
    { value: 'Incharge CCTV', label: 'Incharge CCTV' },
    { value: 'Incharge BAMS', label: 'Incharge BAMS' },
    { value: 'Incharge Guarding', label: 'Incharge Guarding' },
    { value: 'Incharge MIS', label: 'Incharge MIS' },
    { value: 'Incharge HR', label: 'Incharge HR' },
    { value: 'Coordinator', label: 'Coordinator' },
    { value: 'Incharge Building', label: 'Incharge Building' },
];

export function designationToFirestoreValue(selectValue: string): string {
    return String(selectValue || 'PSO')
        .trim()
        .replace(/\s+/g, '_');
}

/** Map stored designation (underscores / legacy) to a `PSO_DESIGNATION_OPTIONS` value. */
export function dbDesignationToSelectValue(raw: string | undefined): string {
    const n = String(raw || '')
        .replace(/_/g, ' ')
        .trim()
        .toLowerCase();
    if (!n) return 'PSO';
    if (n === 'regional pso' || n === 'regional head') return 'Regional Head';
    if (n === 'head pso' || n === 'head') return 'Head';
    for (const o of PSO_DESIGNATION_OPTIONS) {
        const ov = o.value.replace(/_/g, ' ').trim().toLowerCase();
        if (ov === n) return o.value;
    }
    return 'PSO';
}

export async function submitPSO(bankId: string, formData: PSOFormData): Promise<void> {
    if (!bankId) {
        throw new Error('Bank ID is required');
    }

    // Normalize email: trim and convert to lowercase for consistent comparison
    const email = formData.email.trim().toLowerCase();
    const designation = formData.designation.trim();

    if (!formData.password || formData.password.trim().length < 6) {
        throw new Error('Password is required and must be at least 6 characters');
    }
    if (!formData.confirmPassword || formData.password.trim() !== formData.confirmPassword.trim()) {
        throw new Error('Passwords do not match');
    }

    // Save current user session to restore after creating new user
    const currentUser = auth.currentUser;
    if (!currentUser || !currentUser.uid) {
        throw new Error('No user is currently logged in. Please log in as admin to create staff members.');
    }

    const currentUserUid = currentUser.uid;
    const adminUser = currentUser;
    sessionStorage.setItem('creatingUser', currentUserUid);

    let authUid: string;
    try {
        const userCredential = await createUserWithEmailAndPassword(auth, email, formData.password.trim());
        authUid = userCredential.user.uid;

        try {
            await adminUser.reload();
            await updateCurrentUser(auth, adminUser);
        } catch {
            // Keep UI stable via flag; user can refresh if needed.
        }
    } catch (error: any) {
        sessionStorage.removeItem('creatingUser');
        const errorCode = error?.code;
        const errorMessage = error?.message || 'Unknown error occurred';
        if (errorCode === 'auth/email-already-in-use') {
            throw new Error('This email is already registered in the authentication system.');
        } else if (errorCode === 'auth/invalid-email') {
            throw new Error('Invalid email address. Please check the email format.');
        } else if (errorCode === 'auth/weak-password') {
            throw new Error('Password is too weak. Please use a stronger password (at least 6 characters).');
        } else if (errorCode === 'auth/operation-not-allowed') {
            throw new Error('Email/password accounts are not enabled. Please contact an administrator.');
        } else {
            throw new Error(`Failed to create user account: ${errorMessage}`);
        }
    }

    const designationForDB = designationToFirestoreValue(designation);

    const psoData: any = {
        name: formData.name.trim(),
        contact: formData.contact.trim(),
        email: email,
        designation: designationForDB,
        authUid: authUid,
        isActive: true,
        createdAt: Timestamp.now(),
    };

    // Always use authUid as document id so it matches the login uid
    const psoRef = doc(db, 'banks', bankId, 'pos', authUid);
    await setDoc(psoRef, psoData);

    sessionStorage.removeItem('creatingUser');
}

